/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {history} from '@exc/router';
import querystring from 'querystring';

export const BROWSER_BLACKLISTED_PARAMS = [
  '_mr',
  'shell_domain'
];

export const IFRAME_BLACKLISTED_PARAMS = [
  'shell_customtoken',
  'shell_devmode',
  'shell_forcenps',
  'shell_ims',
  'shell_source'
];

/**
 * Cleans any unnecessary query params from the provided path.
 * @param {string} path - The path string to clean.
 * @returns {string} Cleaned path.
 */
export const cleanPath = (path) => {
  const url = new URL(`${window.location.origin}${path}`);
  cleanQueryParams(url.searchParams);
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Removes empty query parameters
 * @param {Object} query - The query object.
 * @returns {Object} Cleaned query object.
 */
export const cleanQuery = (query) => {
  for (let key in query) {
    if (key === '') {
      delete query[key];
    }
  }
  return query;
};

/**
 * Cleans the query params by removing any blacklisted params that
 * exist in the browser URL's query or iframe URL's query. This is to remove
 * duplication of params and any unneeded params.
 * @param {Object} params - `URL.searchParams` object to remove params from.
 */
export const cleanQueryParams = (params) => {
  BROWSER_BLACKLISTED_PARAMS.forEach(param => params.delete(param));
  IFRAME_BLACKLISTED_PARAMS.forEach(param => params.delete(param));
  Object.keys(querystring.parse(window.location.search.slice(1))).forEach(key => {
    params.delete(key);
  });
};

/**
 * Get URL pathname without tenant.
 * @param {string} path - Full URL pathname.
 * @returns {string} Pathname without tenant.
 */
export const getPathWithoutTenant = (path) => {
  return path.replace(/^\/@[^/]*/, '');
};

/**
 * Get query params from string or window location.
 * @param {string} query - Full query string.
 * @returns {Object} Query params and values.
 */
export const getQueryParams = (query) => {
  return query ? querystring.parse(query) : querystring.parse(window.location.search.replace(/^\?/, ''));
};

/**
 * Get the value of a 'shell_' config query param.
 * @param {string} path - Name of shell config param.
 * @returns {string} Value of the query parameter.
 */
export const getQueryValue = (param) => {
  return getQueryParams()[`shell_${param}`];
};

/**
 * Get URL pathname with tenant.
 * @param {string} pathname - Old URL pathname with tenant.
 * @param {string}  path - New path to be used, has no tenant.
 * @param {string} hideTenant - Whether or not to keep tenant in URL.
 * @returns {string} New path updated with or without tenant.
 */
export const getUpdatedPath = (pathname, path, hideTenant = false) => {
  if (!hideTenant) {
    // Adds tenantId from old pathname to path
    path = pathname.replace(/^(\/?@[^/]*)\/.*/, `$1${path}`);
  }
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Compares two paths. This is especially useful because it sorts
 * the search params prior to the comparison since the order in the URL doesn't
 * matter but a string compare will fail.
 * @param {string} path1 - First path to compare.
 * @param {string} path2 - Second path to compare.
 * @returns {Object} Specific sections that have or have not changed.
 */
export const hasChanges = (path1, path2) => {
  const url1 = new URL(`${window.location.origin}${path1}`);
  const url2 = new URL(`${window.location.origin}${path2}`);

  url1.searchParams.sort();
  url2.searchParams.sort();

  const res = {
    hash: url1.hash !== url2.hash,
    pathname: url1.pathname !== url2.pathname,
    search: url1.search !== url2.search
  };
  res.any = res.hash || res.pathname || res.search;
  return res;
};

/**
 * Splits a path with the separator. This returns everything after
 * the first separator and ensures that it starts with a `/` if it did at the
 * beginning.
 * @param {string} path - The path to split.
 * @param {string} separator - The character or string to split on.
 * @returns {string} The remains of the path after the `separator`.
 */
export const splitPath = (path, separator) => {
  const idx = path.indexOf(separator);
  const res = idx > -1 ? path.slice(idx + separator.length) : path;
  return res.startsWith('/') ? res : `/${res}`;
};

class URLManager {
  constructor() {
    this.hasTenant = true;
    this.history = history;
    this.tenant = null;
  }

  /**
   * Track whether the URL should have a tenant or not.
   * If the value has changed, calls updateTenantAndPath to update the URL.
   * @param {boolean} value - Whether or not there is a tenant.
   */
  setUrlHasTenant = (value) => {
    if (this.hasTenant !== value) {
      this.hasTenant = value;
      this.updateTenantAndPath(this.tenant, null, true);
    }
  }

  /**
   * Updates the path with the hash path.
   * @param {string} path - The new path to use for update.
   * @param {Object} router - Router passed in for triggering new route.
   */
  updateHashPath = (path, router) => {
    const {hash} = window.location;
    const pathWithoutTenant = getPathWithoutTenant(path);
    const newHash = cleanPath(getUpdatedPath(hash.replace('#/', ''), pathWithoutTenant, !this.hasTenant));
    router.trigger(newHash.replace(/\/{2,}/g, '/'), true);
  }

  /**
   * Updates path with or without tenant.
   * @param {string} path - New path to be pushed.
   */
  updatePath = (path) => {
    const pathname = this.history.location.pathname || window.location.pathname;
    // This is needed because getUpdatedPath expects the 2nd argument to not
    // have a tenant. path sometimes has a tenant. This prevents it from
    // duplicating tenants in the URL.
    const pathWithoutTenant = getPathWithoutTenant(path);
    history.push(getUpdatedPath(pathname, pathWithoutTenant, !this.hasTenant));
  }

  /**
   * Updates path with new tenant and new path if given.
   * @param {string} tenant - The new tenant to use for update.
   * @param {string} path - The new path to be used for update.
   * @param {boolean} replace - Whether or not to replace vs push to history.
   */
  updateTenantAndPath = (tenant, path, replace) => {
    this.tenant = tenant;
    const {hash, pathname, search} = this.history.location;
    let fullPath = path ? path : `${getPathWithoutTenant(pathname)}${search}${hash}`;
    this.history[replace ? 'replace' : 'push'].call(this.history, this.hasTenant ? `/@${this.tenant}${fullPath}` : fullPath);
  }
}

export default new URLManager;
